import React, { useState, useEffect } from "react";
import { Person, Email, Work, Search } from "@mui/icons-material";
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const ManageTeam = () => {
  const { user, isManager } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [managerInfo, setManagerInfo] = useState(null);

  // Fetch team members
  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/my-team`
      );

      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.teamMembers || []);
        setManagerInfo(data.managerInfo || null);
      } else {
        const error = await response.json();
        console.error("Error:", error.error);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  // Filter team members based on search
  const filteredMembers = teamMembers.filter(member => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    return fullName.includes(searchLower) ||
           (member.email || '').toLowerCase().includes(searchLower);
  });

  // If not a manager, show access denied
  if (!isManager) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">You must be a Collections Manager to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Team</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage and view your collections team members
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Total Team Members</div>
          <div className="text-3xl font-bold text-blue-600">{teamMembers.length}</div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            Showing {filteredMembers.length} of {teamMembers.length} team members
          </div>
        )}
      </div>

      {/* Team Members Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading team members...</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Person className="mx-auto text-gray-400" style={{ fontSize: 64 }} />
          <h3 className="mt-4 text-lg font-semibold text-gray-700">
            {searchTerm ? 'No team members found' : 'No team members assigned yet'}
          </h3>
          <p className="mt-2 text-gray-500">
            {searchTerm
              ? 'Try adjusting your search term'
              : 'Team members will appear here once they are assigned to you'
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((member) => (
            <div
              key={member._id}
              className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow hover:border-blue-300"
            >
              <div className="flex items-start space-x-3">
                <div className="bg-blue-100 p-3 rounded-full flex-shrink-0">
                  <Person className="text-blue-600" style={{ fontSize: 28 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 text-lg truncate">
                    {member.firstName} {member.lastName}
                  </h3>
                  <div className="mt-3 space-y-2">
                    {member.email && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Email fontSize="small" className="mr-2 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{member.email}</span>
                      </div>
                    )}
                    {member.position && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Work fontSize="small" className="mr-2 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{member.position}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <span className="inline-block bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1 rounded-full">
                      {member.roles || 'Collections Agent'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageTeam;
